import movementTypes from 'modules/transactions/constants/movementTypes';
import { useMemo } from 'react';
import { Movement, Operation, Partner, Transaction } from 'types';

type UseGetSplitDataForPartnerProps = {
  splitOperation: Operation | undefined;
  partner: Partner;
  isOldSplit: boolean;
};

const extractOldSplitData = (operation: Operation, partner: Partner) => {
  const movements = (operation?.movements as Movement[]) || [];
  const transactions = (operation?.transactions as Transaction[]) || [];

  const partnerSharesCount: number = movements.reduce((total, movement) => {
    if (
      (movement.partner as Partner)?._id === partner._id &&
      movement.movementType === movementTypes.DECREMENT
    ) {
      return total + (movement?.shareTo - movement?.shareFrom + 1 || 0);
    }
    return total;
  }, 0);

  const partnerNewSharesCount: number = transactions.reduce(
    (total, transaction) => {
      if ((transaction.partner as Partner)?._id === partner._id) {
        return total + (transaction?.shareTo - transaction?.shareFrom + 1 || 0);
      }
      return total;
    },
    0
  );

  return {
    sharesCount: partnerSharesCount,
    newSharesCount: partnerNewSharesCount,
  };
};

const extractNewSplitData = (operation: Operation, partner: Partner) => {
  const { splits = [] } = partner;

  const currentSplit = splits.find(
    (split) => split.operation === operation?._id
  );

  const sharesCount = currentSplit?.sharesCount || 0;
  const newSharesCount = currentSplit?.newSharesCount || 0;

  return {
    sharesCount,
    newSharesCount,
  };
};

export const useGetSplitDataForPartner = ({
  splitOperation,
  partner,
  isOldSplit,
}: UseGetSplitDataForPartnerProps) => {
  const data = useMemo(() => {
    if (!splitOperation) {
      return {
        sharesCount: 0,
        newSharesCount: 0,
      };
    }

    return isOldSplit
      ? extractOldSplitData(splitOperation, partner)
      : extractNewSplitData(splitOperation, partner);
  }, [splitOperation, partner, isOldSplit]);

  return { data };
};
